import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { getFeatureById, getFeatureByName } from '../constants/featureRegistry'; // Added getFeatureByName

/**
 * Elasticsearch API utilities for the frontend
 * Replaces Firestore queries with Elasticsearch-powered search
 */

// Initialize callable functions
const searchSitesCallable = httpsCallable(functions, 'searchSitesFunction');
const getSuggestionsCallable = httpsCallable(functions, 'getSuggestionsFunction');
const getFilterAggregationsCallable = httpsCallable(functions, 'getFilterAggregations');
const getSearchStatsCallable = httpsCallable(functions, 'getSearchStats');
const checkSiteNameExistsCallable = httpsCallable(functions, 'checkSiteNameExists');

/**
 * Search sites using Elasticsearch
 * @param {Object} params - Search parameters
 * @param {string} params.query - Text query for site names/descriptions
 * @param {string} params.location - Location query
 * @param {Object} params.filters - Filter object
 * @param {number} params.page - Page number (0-based)
 * @param {number} params.size - Number of results per page
 * @param {boolean} params.includeAggregations - Whether to include filter aggregations
 * @returns {Promise<Object>} Search results
 */
export const searchSites = async (params = {}) => {
  try {
    const {
      query = '',
      location = '',
      filters = {},
      page = 0,
      size = 20,
      includeAggregations = true
    } = params;

    const result = await searchSitesCallable({
      query,
      location,
      filters,
      page,
      size,
      includeAggregations
    });

    if (!result.data) {
      console.error('No data returned from search function');
      return {
        success: false,
        error: 'No data returned from search',
        data: [],
        total: 0,
        aggregations: {}
      };
    }

    if (result.data.success) {
      return {
        success: true,
        data: result.data.data?.sites || [],
        total: result.data.data?.total || 0,
        page: result.data.data?.page || 0,
        size: result.data.data?.size || 20,
        hasMore: result.data.data?.hasMore || false,
        aggregations: result.data.data?.aggregations || {}
      };
    }

    console.error('Search failed:', result.data.error);
    return {
      success: false,
      error: result.data.error || 'Search failed',
      data: [],
      total: 0,
      aggregations: {}
    };
  } catch (error) {
    console.error('Error searching sites:', error);
    return {
      success: false,
      error: error.message || 'Failed to search sites',
      data: [],
      total: 0,
      aggregations: {}
    };
  }
};

/**
 * Get autocomplete suggestions
 * @param {string} query - Query to get suggestions for
 * @param {string} field - Field to get suggestions from (default: 'name')
 * @returns {Promise<Array>} Array of suggestions
 */
export const getSuggestions = async (query, field = 'name') => {
  try {
    if (!query || query.length < 2) {
      return [];
    }

    const result = await getSuggestionsCallable({
      query,
      field
    });

    if (result.data.success) {
      return result.data.data;
    } else {
      throw new Error(result.data.error || 'Suggestions failed');
    }
  } catch (error) {
    console.error('Error getting suggestions:', error);
    return [];
  }
};

/**
 * Get filter aggregations (counts for each filter option)
 * @param {Object} filters - Current filters to apply
 * @returns {Promise<Object>} Aggregations object
 */
export const getFilterAggregations = async (filters = {}) => {
  try {
    const result = await getFilterAggregationsCallable({
      filters
    });

    // Log the raw response from the Firebase function
    console.log('Raw Elasticsearch Aggregations Response (from Firebase function):', JSON.stringify(result.data, null, 2));

    if (result.data.success) {
      return {
        success: true,
        data: result.data.data.aggregations,
        total: result.data.data.total
      };
    } else {
      throw new Error(result.data.error || 'Aggregations failed');
    }
  } catch (error) {
    console.error('Error getting aggregations:', error);
    return {
      success: false,
      data: {},
      total: 0
    };
  }
};

/**
 * Get search statistics
 * @returns {Promise<Object>} Search statistics
 */
export const getSearchStats = async () => {
  try {
    const result = await getSearchStatsCallable();

    if (result.data.success) {
      return {
        success: true,
        data: result.data.data
      };
    } else {
      throw new Error(result.data.error || 'Stats retrieval failed');
    }
  } catch (error) {
    console.error('Error getting search stats:', error);
    return {
      success: false,
      data: {
        totalSites: 0,
        countries: 0,
        features: 0,
        cultures: 0,
        eras: 0,
        topCountries: [],
        topFeatures: []
      }
    };
  }
};

/**
 * Helper function to transform search parameters from the current Search.jsx format
 * to the format expected by Elasticsearch
 */
export const transformSearchParams = (searchParams, filters) => {
  const params = {
    query: searchParams.get('name') || '',
    location: searchParams.get('location') || '',
    filters: {},
    page: 0,
    size: 20,
    includeAggregations: true
  };

  // Transform filters to Elasticsearch format
  if (filters.features && filters.features.length > 0) {
    // Send the original feature names to Elasticsearch without normalization
    params.filters.features = filters.features;
  }

  if (filters.era) {
    params.filters.era = filters.era;
  }

  if (filters.materialType) {
    params.filters.materialType = filters.materialType;
  }

  if (filters.verificationStatus) {
    params.filters.verificationStatus = filters.verificationStatus;
  }

  if (filters.countries && filters.countries.length > 0) {
    params.filters.countries = filters.countries;
  }

  if (filters.featureFilterLogic) {
    params.filters.featureFilterLogic = filters.featureFilterLogic;
  }

  // params.filters.categories is removed as per user request to simplify filtering to direct feature selection
  // if (filters.categories && filters.categories.length > 0) { 
  //   params.filters.categories = filters.categories;
  // }

  return params;
};

/**
 * Helper function to transform Elasticsearch results back to the format
 * expected by current components (SiteCard, etc.)
 */
export const transformSearchResults = (elasticsearchResults) => {
  if (!Array.isArray(elasticsearchResults)) {
    console.error('Expected array for elasticsearchResults, got:', typeof elasticsearchResults);
    return [];
  }

  return elasticsearchResults.map(site => {
    // Ensure we have valid site data
    if (!site || !site.id) {
      console.warn('Invalid site data:', site);
      return null;
    }

    // Transform media from the Elasticsearch structure
    const transformedMedia = [];
    
    // Process media_preview array from Elasticsearch
    if (site.media_preview && Array.isArray(site.media_preview)) {
      site.media_preview.forEach(mediaItem => {
        if (!mediaItem || !mediaItem.url) {
          console.warn('Skipping invalid media item:', mediaItem);
          return;
        }

        let predictions = [];
        // Check if new 'predictions' field (array of objects with coordinates) exists and seems valid
        if (mediaItem.predictions && Array.isArray(mediaItem.predictions) && mediaItem.predictions.length > 0 && mediaItem.predictions[0].hasOwnProperty('x')) {
          predictions = mediaItem.predictions;
        } else if (mediaItem.features && Array.isArray(mediaItem.features)) {
          // Fallback to old 'features' field (array of strings) if new 'predictions' is not available/valid
          // This will populate the feature list, but bounding boxes will be at 0,0.
          mediaItem.features.forEach(featureName => {
            if (featureName) {
              predictions.push({
                class: featureName,
                source: 'indexed_fallback', // Mark as fallback data
                confidence: 1.0,
                x: 0, // Default coordinates
                y: 0,
                width: 0,
                height: 0
              });
            }
          });
        }

        // Enhanced media ID handling for feature tagging compatibility
        let mediaId = null;
        
        // Priority order for finding the correct Firestore document ID:
        // 1. firestoreId (explicit Firestore document ID)
        // 2. docId (alternative field name for Firestore document ID)
        // 3. mediaId (might be Firestore document ID)
        // 4. id (fallback, might be Firestore document ID)
        if (mediaItem.firestoreId) {
          mediaId = mediaItem.firestoreId;
        } else if (mediaItem.docId) {
          mediaId = mediaItem.docId;
        } else if (mediaItem.mediaId) {
          mediaId = mediaItem.mediaId;
        } else if (mediaItem.id) {
          mediaId = mediaItem.id;
        }

        // Log warning if we couldn't find a proper ID
        if (!mediaId) {
          console.warn('No valid ID found for media item:', mediaItem);
        }

        transformedMedia.push({
          id: mediaId, // Use the best available ID for Firestore operations
          url: mediaItem.url,
          type: mediaItem.type || 'image',
          name: mediaItem.title || mediaItem.name || '', // Prefer title
          predictions: predictions,
          // Populate 'features' (array of strings) for compatibility with any components still using it
          features: (Array.isArray(predictions) && predictions.length > 0) 
                      ? predictions.map(p => p.class) 
                      : (mediaItem.features || [])
        });
      });
    }

    // Ensure coordinates are properly formatted
    let coordinates = null;
    if (site.location?.coordinates) {
      const coords = site.location.coordinates;
      if (coords.lat != null && coords.lon != null) {
        coordinates = {
          lat: Number(coords.lat),
          lng: Number(coords.lon)
        };
      } else if (coords.latitude != null && coords.longitude != null) {
        coordinates = {
          lat: Number(coords.latitude),
          lng: Number(coords.longitude)
        };
      }
    }

    // Transform features from Elasticsearch format to SiteCard expected format
    const siteFeatures = {};
    if (site.features && typeof site.features === 'object') {
      // If site.features is already in the expected object format
      Object.assign(siteFeatures, site.features);
    }
    
    // Also extract features from media predictions for comprehensive feature list
    const mediaFeatures = new Map();
    transformedMedia.forEach(media => {
      if (media.predictions && Array.isArray(media.predictions)) {
        media.predictions.forEach(pred => {
          if (pred.class && !mediaFeatures.has(pred.class)) {
            mediaFeatures.set(pred.class, {
              enabled: true,
              autoDetected: pred.source === 'auto',
              confidence: pred.confidence || 1,
              label: pred.label || pred.class,
              category: pred.category || 'uncategorized'
            });
          }
        });
      }
    });
    
    // Merge site features with media features, prioritizing site features
    mediaFeatures.forEach((feature, featureId) => {
      if (!siteFeatures[featureId]) {
        siteFeatures[featureId] = feature;
      }
    });

    return {
      id: site.id,
      name: site.name || '',
      description: site.description || '',
      location: {
        address: site.location?.address || '',
        country: site.location?.country || '',
        region: site.location?.region || '',
        coordinates: coordinates
      },
      features: siteFeatures, // This is what SiteCard expects
      features_normalized: Object.keys(siteFeatures), // Legacy field
      mainImageUrl: transformedMedia[0]?.url || null,
      media: transformedMedia,
      mediaCount: site.media_count || transformedMedia.length,
      culture: site.culture || '',
      startEra: site.start_era || '',
      endEra: site.end_era || '',
      estimatedEra: site.estimatedEra || '',
      featured: !!site.featured,
      verificationStatus: site.verification_status || 'pending',
      // Add search-specific fields
      _score: site.score,
      _aggregations: site.aggregations,
      // Add sublocations transformation
      sublocations: (site.sublocations && Array.isArray(site.sublocations))
        ? site.sublocations.map(sub => ({
            sublocationId: sub.sublocationId,
            name: sub.name || '',
            coordinates: (sub.coordinates && sub.coordinates.lat != null && sub.coordinates.lon != null)
              ? { lat: Number(sub.coordinates.lat), lng: Number(sub.coordinates.lon) } // Convert lon to lng
              : null,
            // description: sub.description || '', // Include if available and needed
            // type: sub.type || '', // Include if available and needed
          }))
        : [],
    };
  }).filter(Boolean); // Remove any null entries
};

/**
 * Helper function to extract filter options from aggregations
 */
export const extractFilterOptions = (aggregations) => {
  const options = {};

  if (aggregations.features?.features?.buckets) {
    options.features = aggregations.features.features.buckets.map(bucket => {
      const keyFromAgg = bucket.key; // This could be an ID or a label
      let featureDetails = getFeatureById(keyFromAgg); // Try matching by ID first

      if (!featureDetails) {
        // If not found by ID, try by name (label), assuming keyFromAgg might be a label
        featureDetails = getFeatureByName(keyFromAgg);
      }
      
      // If still not found, try a lowercase version of the key as a name
      if (!featureDetails) {
        featureDetails = getFeatureByName(keyFromAgg.toLowerCase());
      }

      return {
        id: featureDetails ? featureDetails.id : keyFromAgg, // Use registry ID if found, else keep original key
        label: featureDetails ? featureDetails.label : keyFromAgg, // Use registry label if available
        category: featureDetails ? featureDetails.category : 'uncategorized', // Use registry category
        count: bucket.doc_count
      };
    });
  }

  // Removed extraction of separate category aggregations as per user request
  // if (aggregations.categories?.categories?.categories?.buckets) {
  //   options.categories = aggregations.categories.categories.categories.buckets.map(bucket => ({
  //     id: bucket.key, 
  //     label: bucket.key.charAt(0).toUpperCase() + bucket.key.slice(1), 
  //     count: bucket.doc_count
  //   }));
  // }

  if (aggregations.countries?.buckets) {
    options.countries = aggregations.countries.buckets.map(bucket => ({
      id: bucket.key,
      label: bucket.key,
      count: bucket.doc_count
    }));
  }

  if (aggregations.eras?.buckets) {
    options.eras = aggregations.eras.buckets.map(bucket => ({
      id: bucket.key,
      label: bucket.key,
      count: bucket.doc_count
    }));
  }

  if (aggregations.cultures?.buckets) {
    options.cultures = aggregations.cultures.buckets.map(bucket => ({
      id: bucket.key,
      label: bucket.key,
      count: bucket.doc_count
    }));
  }

  if (aggregations.material_types?.buckets) {
    options.materialTypes = aggregations.material_types.buckets.map(bucket => ({
      id: bucket.key,
      label: bucket.key,
      count: bucket.doc_count
    }));
  }

  if (aggregations.verification_statuses?.buckets) {
    options.verificationStatuses = aggregations.verification_statuses.buckets.map(bucket => ({
      id: bucket.key,
      label: bucket.key,
      count: bucket.doc_count
    }));
  }

  return options;
};

/**
 * Check if a site name already exists (exact match).
 * @param {string} name - The site name to check.
 * @returns {Promise<Object>} Object containing exists (boolean) and siteId (if found).
 */
export const checkSiteNameExists = async (name) => {
  try {
    if (!name || name.trim().length < 3) {
      return { exists: false, siteId: null };
    }

    const result = await checkSiteNameExistsCallable({ name });

    if (result.data.success) {
      return {
        exists: result.data.data.exists,
        siteId: result.data.data.siteId || null,
      };
    } else {
      throw new Error(result.data.error || 'Failed to check site name');
    }
  } catch (error) {
    console.error('Error checking site name:', error);
    // Return a non-blocking response in case of error
    return { exists: false, siteId: null, error: error.message };
  }
};
